import { useEffect } from "react";
import { absoluteUrl, DEFAULT_DESC, DEFAULT_IMAGE, DEFAULT_TITLE } from "@/lib/seo";
import { SEO_DEFAULTS, SITE } from "@/config/site.config";

/**
 * Per-page document head.
 *
 * Sets title, description, canonical, Open Graph and Twitter tags, and injects any
 * page-specific JSON-LD. Every tag it writes is marked `data-seo` so it can be cleaned
 * up on route change — otherwise stale structured data from the previous page lingers.
 *
 * The build-time prerenderer (`scripts/prerender.js`) snapshots the DOM after this has
 * run, so crawlers receive these tags in the initial HTML rather than after hydration.
 */

/**
 * Head text is written plainly: a spaced em dash reads as machine-made in a browser tab, a
 * search result or a WhatsApp card, where a hyphen is how people actually type. Applied here,
 * once, to everything that goes into <head>, so no page title can bring one back.
 */
const plain = (text) => (text ? text.replace(/\s+—\s+/g, " - ").replace(/—/g, "-") : text);

const upsertMeta = (key, attr, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEOHead = ({
  title,
  description = DEFAULT_DESC,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd = [],
  /**
   * What a shared link shows (WhatsApp, LinkedIn, X), when that should differ from the
   * browser tab and the search result. The <title> is written to be found; the share
   * title is written to be tapped by someone who was just sent the link.
   */
  ogTitle,
  ogDescription,
  imageAlt,
}) => {
  const fullTitle = plain(title ? `${title} - SNAPX TECHNOLOGIES LLP` : DEFAULT_TITLE);
  const url = absoluteUrl(path);
  const shareTitle = plain(ogTitle) || fullTitle;
  const shareDescription = plain(ogDescription || description);
  const metaDescription = plain(description);
  // Derived from the file, not assumed: the share card is a JPEG now (its gradients make
  // the same picture three times heavier as a PNG) and a wrong type is ignored by some scrapers.
  const imageType = /\.jpe?g(\?|$)/i.test(image) ? "image/jpeg" : "image/png";
  // Serialise so the effect re-runs when schema content changes, not on every render.
  const schemaKey = JSON.stringify(jsonLd);

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta("description", "name", metaDescription);
    // A staging deploy (REACT_APP_INDEXABLE=false) is noindex everywhere, regardless
    // of what an individual page asked for.
    const blocked = noindex || !SEO_DEFAULTS.indexable;
    upsertMeta("robots", "name", blocked ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1");

    upsertMeta("og:title", "property", shareTitle);
    upsertMeta("og:description", "property", shareDescription);
    upsertMeta("og:url", "property", url);
    upsertMeta("og:type", "property", type);
    upsertMeta("og:image", "property", image);
    // Without explicit dimensions several platforms fall back to a small thumbnail
    // rather than the large card, and WhatsApp may skip the image on first fetch.
    upsertMeta("og:image:width", "property", String(SITE.ogImageWidth));
    upsertMeta("og:image:height", "property", String(SITE.ogImageHeight));
    upsertMeta("og:image:type", "property", imageType);
    upsertMeta("og:image:alt", "property", imageAlt || shareTitle);
    upsertMeta("og:site_name", "property", SITE.name);
    upsertMeta("og:locale", "property", "en_IN");

    upsertMeta("twitter:card", "name", "summary_large_image");
    upsertMeta("twitter:title", "name", shareTitle);
    upsertMeta("twitter:description", "name", shareDescription);
    upsertMeta("twitter:image", "name", image);
    upsertMeta("twitter:image:alt", "name", imageAlt || shareTitle);

    upsertLink("canonical", url);
  }, [fullTitle, metaDescription, url, type, image, noindex, shareTitle, shareDescription, imageType, imageAlt]);

  useEffect(() => {
    const blocks = JSON.parse(schemaKey);
    if (!blocks.length) return undefined;
    const nodes = blocks.map((block) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo", "page");
      el.textContent = JSON.stringify(block);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((el) => el.remove());
  }, [schemaKey]);

  return null;
};

export default SEOHead;
